import React, { useState } from 'react';

import {
  JOURNEY_TEAMS_CSV_IMPORT_ERROR_OUTCOMES,
  JOURNEY_TEAMS_CSV_IMPORT_MISSING_LEARNER_REASON,
  JOURNEY_TEAMS_CSV_IMPORT_PAGE_SIZE,
  JOURNEY_TEAMS_CSV_IMPORT_ROW_REASON,
} from '@/features/csvImport/admin/constants/journeyTeamsCsvImport';
import { CsvImportBucketGrid } from '@/features/csvImport/admin/molecules/CsvImportBucketGrid';
import { CsvImportBucket, CsvImportBucketGridColumn } from '@/features/csvImport/admin/types/csvImport';
import {
  JourneyTeamsCsvImportBlockingRow,
  JourneyTeamsCsvImportRow,
  toJourneyTeamsCsvImportRow,
} from '@/features/csvImport/admin/types/journeyTeamsCsvImport';
import { RLearningJourneyTeamImportRowOutcome } from '@/store/v2';
import { LearningJourneyTeamImportPreview } from '@/store/v2/journeys/teamImport';

const EMAIL_COLUMNS: CsvImportBucketGridColumn<{ email: string }>[] = [{ header: 'Email', render: ({ email }) => email }];

const EMAIL_AND_TEAM_COLUMNS: CsvImportBucketGridColumn<JourneyTeamsCsvImportRow>[] = [
  { header: 'Email', render: ({ email }) => email, width: '60%' },
  { header: 'Team', render: ({ teamName }) => teamName },
];

const REJECTED_COLUMNS: CsvImportBucketGridColumn<JourneyTeamsCsvImportBlockingRow>[] = [
  { header: 'Email', render: ({ email }) => email, width: '60%' },
  { header: 'Reason', render: ({ reason }) => reason },
];

const getPage = <Row,>(rows: Row[], page: number): Row[] =>
  rows.slice(page * JOURNEY_TEAMS_CSV_IMPORT_PAGE_SIZE, (page + 1) * JOURNEY_TEAMS_CSV_IMPORT_PAGE_SIZE);

const getPageCount = (total: number): number => Math.ceil(total / JOURNEY_TEAMS_CSV_IMPORT_PAGE_SIZE);

type JourneyTeamsCsvImportBuckets = {
  /** In the order the review screen shows them, worst outcome first. */
  buckets: CsvImportBucket[];
  /**
   * Rejected rows followed by the learners left without a team — everything the admin has to deal
   * with, as the combined download offers it. Empty when there is nothing to download.
   */
  blockingRows: JourneyTeamsCsvImportBlockingRow[];
};

/**
 * Turns one team-import preview into the review screen's buckets.
 *
 * Three of the four read the file: the Errors bucket merges the three row outcomes that are the
 * file's fault, and Not in this journey and Ready for set up each stand for one outcome.
 *
 * Missing a team does not. It lists `missingLearners` — journey learners the file leaves without a
 * team, whether it never named them or named them in a row that failed — because that, not the
 * `missingTeam` row count, is both what the admin has to fix and what the server's gate is
 * computed from. A learner whose row was rejected therefore shows up twice, once under the row's
 * own error and once here, which is correct: two different things need doing about them.
 *
 * Counts come from `stats` for the file-backed buckets, which is what makes the Errors merge safe.
 * Page state is kept per bucket so reopening a section lands where it was left.
 */
export const useJourneyTeamsCsvImportBuckets = (preview: LearningJourneyTeamImportPreview): JourneyTeamsCsvImportBuckets => {
  const [errorsPage, setErrorsPage] = useState(0);
  const [missingTeamPage, setMissingTeamPage] = useState(0);
  const [notInJourneyPage, setNotInJourneyPage] = useState(0);
  const [readyPage, setReadyPage] = useState(0);

  const { stats, rows, missingLearners } = preview;

  const errorRows: JourneyTeamsCsvImportBlockingRow[] = rows
    .filter(({ outcome }) => JOURNEY_TEAMS_CSV_IMPORT_ERROR_OUTCOMES.includes(outcome))
    .map((row) => ({ ...toJourneyTeamsCsvImportRow(row), reason: JOURNEY_TEAMS_CSV_IMPORT_ROW_REASON[row.outcome] || '' }));

  const notInJourneyRows = rows
    .filter(({ outcome }) => outcome === RLearningJourneyTeamImportRowOutcome.NotInJourney)
    .map(toJourneyTeamsCsvImportRow);

  const readyRows = rows
    .filter(({ outcome }) => outcome === RLearningJourneyTeamImportRowOutcome.Ready)
    .map(toJourneyTeamsCsvImportRow);

  // A learner has no source line to be keyed on — they may have no row at all — so the address
  // stands in. It is unique here: the list is one entry per journey learner.
  const missingLearnerRows: JourneyTeamsCsvImportBlockingRow[] = missingLearners.map(({ email }) => ({
    key: email,
    email,
    teamName: '',
    reason: JOURNEY_TEAMS_CSV_IMPORT_MISSING_LEARNER_REASON,
  }));

  const errorsCount = stats.invalidEmail + stats.duplicateInFile + stats.missingRequiredField;

  const buckets: CsvImportBucket[] = [
    {
      id: 'errors',
      label: 'Errors',
      summaryLabel: 'errors',
      icon: ['far', 'circle-x'],
      variant: 'error',
      count: errorsCount,
      renderContent: () => (
        <CsvImportBucketGrid
          columns={REJECTED_COLUMNS}
          rows={getPage(errorRows, errorsPage)}
          rowKey={({ key }) => key}
          pagination={{ page: errorsPage, pageCount: getPageCount(errorsCount), onPageChange: setErrorsPage }}
        />
      ),
    },
    {
      id: 'missing-a-team',
      label: 'Missing a team',
      summaryLabel: 'missing a team',
      icon: ['far', 'circle-exclamation'],
      variant: 'warning',
      count: missingLearners.length,
      renderContent: () => (
        <CsvImportBucketGrid
          columns={EMAIL_COLUMNS}
          rows={getPage(missingLearnerRows, missingTeamPage)}
          rowKey={({ key }) => key}
          pagination={{
            page: missingTeamPage,
            pageCount: getPageCount(missingLearners.length),
            onPageChange: setMissingTeamPage,
          }}
        />
      ),
    },
    {
      id: 'not-in-this-journey',
      label: 'Not in this journey',
      summaryLabel: 'not in this journey',
      icon: ['far', 'circle-minus'],
      variant: 'neutral',
      count: stats.notInJourney,
      renderContent: () => (
        <CsvImportBucketGrid
          columns={EMAIL_COLUMNS}
          rows={getPage(notInJourneyRows, notInJourneyPage)}
          rowKey={({ key }) => key}
          pagination={{ page: notInJourneyPage, pageCount: getPageCount(stats.notInJourney), onPageChange: setNotInJourneyPage }}
        />
      ),
    },
    {
      id: 'ready-for-set-up',
      label: 'Ready for set up',
      summaryLabel: 'ready for set up',
      icon: ['far', 'circle-check'],
      variant: 'info',
      count: stats.ready,
      renderContent: () => (
        <CsvImportBucketGrid
          columns={EMAIL_AND_TEAM_COLUMNS}
          rows={getPage(readyRows, readyPage)}
          rowKey={({ key }) => key}
          pagination={{ page: readyPage, pageCount: getPageCount(stats.ready), onPageChange: setReadyPage }}
        />
      ),
    },
  ];

  return { buckets, blockingRows: [...errorRows, ...missingLearnerRows] };
};
